import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Inbox } from "lucide-react"

import { api, ErrorApi } from "@/api/cliente"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

/**
 * Quién lleva cuánto.
 *
 * ⚠ **Ésta es la vista «por responsable» que se retiró del tablero, y vuelve porque
 * ahora sí tiene sentido.** Se retiró porque agrupar por persona sólo dice algo
 * cuando se ven las tareas de todos: quien sólo ve las suyas obtenía una columna con
 * su nombre encima. Esta versión pide `tarea:asignar` —el permiso que ya marca
 * «coordina»— y por eso siempre mira el equipo entero; y no repinta el tablero por
 * persona, sino que cuenta, que es lo que se necesita para decidir a quién le toca
 * lo siguiente.
 *
 * ⚠ **Por `componentes/ui/Tabla`, como todas.** Nació con un `<table>` propio y fue
 * la única tabla del sistema fuera del componente canónico: sin superficie, sin
 * cabecera teñida y sin separadores, o sea una rejilla de cifras flotando sobre el
 * lienzo. Ese componente existe exactamente para que eso no pase —lo dice en su
 * cabecera: «antes había doce tablas y doce estilos»— y escribir una nueva a mano
 * es cómo se vuelve a doce.
 *
 * ⚠ **La fila «Sin dueño» es trabajo real.** Es lo abierto que no le pesa a nadie, y
 * esconderlo es exactamente perderlo de vista: en un reparto por persona, esa fila
 * es la que se llena sola.
 *
 * ⚠ **La barra es proporcional al más cargado del equipo, no a un número fijo.** Lo
 * que se compara aquí es entre personas; una escala absoluta pintaría a todo el mundo
 * casi vacío en una semana floja y casi lleno en una punta, y en las dos deja de
 * decir quién está peor.
 */

type Carga = {
  usuario_id: number | null
  nombre: string
  abiertas: number
  vencidas: number
  esta_semana: number
}

type Respuesta = { items: Carga[] }

export function CargaEquipo({ onVer }: { onVer: (usuarioId: number) => void }) {
  const consulta = useQuery({
    queryKey: ["panel", "carga"],
    queryFn: () => api.get<Respuesta>("/api/v1/panel/carga"),
    refetchInterval: 60_000,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (consulta.isError) {
    return (
      <p
        role="alert"
        className="flex items-center gap-2 text-sm"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} aria-hidden={true} />
        {consulta.error instanceof ErrorApi
          ? consulta.error.message
          : "No se pudo cargar la carga del equipo"}
      </p>
    )
  }

  const items = consulta.data?.items ?? []
  const tope = Math.max(...items.map((c) => c.abiertas), 1)

  return (
    <Tabla etiqueta="Tareas abiertas por responsable">
      <Cabecera>
        <Th>Responsable</Th>
        <Th derecha>Abiertas</Th>
        <Th derecha>Vencidas</Th>
        <Th derecha>Esta semana</Th>
        <Th className="w-1/3" />
      </Cabecera>
      <Cuerpo>
        {items.length === 0 ? (
          <TablaVacia colSpan={5}>
            <span className="flex flex-col items-center gap-2">
              <Inbox size={20} aria-hidden={true} />
              No hay tareas abiertas.
            </span>
          </TablaVacia>
        ) : (
          items.map((c) => (
            <Fila key={c.usuario_id ?? "sin-dueno"} data-testid={`carga-${c.usuario_id ?? "sin"}`}>
              {/* El nombre es un botón y la exportación salta los botones. */}
              <Td data-exportar-valor={c.nombre}>
                {c.usuario_id === null ? (
                  <span style={{ color: "var(--fg-muted)" }}>{c.nombre}</span>
                ) : (
                  // El nombre lleva a sus tareas: un contador que no abre lo que
                  // cuenta obliga a repetir el filtro a mano en la otra vista.
                  <button
                    type="button"
                    onClick={() => onVer(c.usuario_id as number)}
                    className="underline-offset-2 hover:underline"
                    style={{ color: "var(--primary-texto)" }}
                  >
                    {c.nombre}
                  </button>
                )}
              </Td>
              <Td numero derecha>
                {c.abiertas}
              </Td>
              <Td numero derecha>
                <span style={{ color: c.vencidas > 0 ? "var(--destructive)" : undefined }}>
                  {c.vencidas}
                </span>
              </Td>
              <Td numero derecha>
                {c.esta_semana}
              </Td>
              <Td>
                {/* Decorativa: los tres números de al lado ya dicen lo mismo, y quien
                    use lector de pantalla no necesita oír una barra. */}
                <div
                  aria-hidden={true}
                  className="h-2 rounded-full"
                  style={{ backgroundColor: "var(--surface-elevated)" }}
                >
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${(c.abiertas / tope) * 100}%`,
                      backgroundColor: c.vencidas > 0 ? "var(--destructive)" : "var(--primary)",
                    }}
                  />
                </div>
              </Td>
            </Fila>
          ))
        )}
      </Cuerpo>
    </Tabla>
  )
}
